import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Fade,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";

const DownloadCVModal = ({ open, onClose, onSubmit, loading, developerName }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({ name: "", email: "" });

  const validate = () => {
    const newErrors = { name: "", email: "" };
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = "Name is required.";
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        newErrors.email = "Please enter a valid email address.";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(name.trim(), email.trim());
  };

  // Clear field error on change
  const handleNameChange = (e) => {
    setName(e.target.value);
    if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={400}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        },
      }}
    >
      <Box sx={{ pb: 3 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: "background.paper", borderRadius: "24px 24px 0 0", pt: 4 }}>
        <Box display="flex" flexDirection="column" gap={2.5} sx={{ pb: 1 }}>
          <TextField
            fullWidth
            label="Your Name"
            placeholder="John Doe"
            value={name}
            onChange={handleNameChange}
            disabled={loading}
            error={!!errors.name}        // ← red border on name
            helperText={errors.name}     // ← error text under name
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                transition: "0.2s",
                "&:hover": { bgcolor: "grey.50" },
                "&.Mui-focused": { bgcolor: "background.paper" },
              },
            }}
          />

          <TextField
            fullWidth
            label="Email Address"
            placeholder="john@example.com"
            type="email"
            value={email}
            onChange={handleEmailChange}
            disabled={loading}
            error={!!errors.email}       // ← red border on email
            helperText={errors.email}    // ← error text under email
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MailOutlineIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                transition: "0.2s",
                "&:hover": { bgcolor: "grey.50" },
                "&.Mui-focused": { bgcolor: "background.paper" },
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
              boxShadow: "0 10px 15px -3px rgba(20, 184, 166, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
                boxShadow: "0 20px 25px -5px rgba(20, 184, 166, 0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Download Profile Now"
            )}
          </Button>

          <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ px: 2, mt: 1 }}>
            By clicking download, you agree to receive follow-up communication regarding this developer.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DownloadCVModal;